/**
 * ThreadRepliesIndicator — Shown below a message bubble when replyCount > 0
 * Displays reply count + last reply preview. Clickable to open thread.
 */

import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ChatMessage } from '../types';

interface ThreadRepliesIndicatorProps {
  message: ChatMessage;
  onOpenThread: (message: ChatMessage) => void;
}

export const ThreadRepliesIndicator = memo(function ThreadRepliesIndicator({
  message,
  onOpenThread,
}: ThreadRepliesIndicatorProps) {
  const { t } = useTranslation();

  if (!message.replyCount || message.replyCount === 0) return null;

  const timeAgo = message.lastReplyAt
    ? getRelativeTime(new Date(message.lastReplyAt))
    : '';

  return (
    <button
      type="button"
      onClick={() => onOpenThread(message)}
      className="mt-0.5 ms-10 flex items-center gap-1.5 rounded-lg px-2 py-1 text-start transition-colors hover:bg-blue-50/50"
    >
      <svg className="h-3.5 w-3.5 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
      <span className="text-xs font-medium text-blue-600">
        {t('messaging.threadReplies', { count: message.replyCount })}
      </span>
      {message.lastReplySenderName && (
        <span className="text-[11px] text-neutral-500">
          · {message.lastReplySenderName} {timeAgo}
        </span>
      )}
    </button>
  );
});

function getRelativeTime(date: Date): string {
  const now = Date.now();
  const diffMs = now - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);

  if (diffMin < 1) return '';
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay}d`;
}
